import './applianceSpecs.scss';
import React, { useRef } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { IAppliance } from '../../interfaces';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

export default function ApplianceSpecs(props: { appliance: IAppliance }) {
    const { appliance } = props;
    const { currentLocale } = usePageLocalization();
    const { clearanceFooter } = useStaticQuery(
        graphql`
            query {
                clearanceFooter: allProductsJson(
                    filter: { code: { eq: "englander_clearance-footer" } }
                ) {
                    nodes {
                        code
                        attributes {
                            content {
                                values {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                                valuesMarkdownRemark {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                            }
                        }
                    }
                }
            }
        `
    );
    const { t } = useTranslation('layout');
    const isMobile = useMobileDetect().isMobile();
    const isTablet = useMobileDetect().isTablet();

    const allowDisplay = (value: string) => {
        switch (value) {
            case 'available_country':
            case 'faq_mfp':
            case 'marque':
            case 'meta_description':
            case 'meta_title':
            case 'shipping_std_included':
            case 'size':
            case 'status':
            case 'suggested_appliances':
            case 'upc':
            case 'video_ids':
            case 'description_courte':
            case 'visao_id':
            case 'vuesIPEP': {
                return false;
            }
            default: {
                return true;
            }
        }
    };

    const specificationMap = new Map();
    const textndIds = new Map();
    let noteIndex = 1;
    Array.from(specificationMap).forEach((spec: any) => {
        spec[1]?.options?.values.forEach((text: any) => {
            if (!textndIds.get(text?.id) && isPresentInValues(spec[0])) {
                textndIds.set(text?.id, { text, index: noteIndex });
                noteIndex++;
            }
        });
    });
    const notesReff = useRef(null);
    const scrollToRef = (ref: any) => {
        if (typeof window !== 'undefined') {
            window.scrollTo({
                left: 0,
                top: ref.current.offsetTop - window.innerHeight / 2,
                behavior: 'smooth',
            });
        }
    };

    const allAttributes = appliance?.attributes;
    const attributesNotNull = Object.entries(allAttributes).filter(
        (attribute: [string, any]) => attribute[1] !== null
    );
    const currentAttributes = attributesNotNull.sort(
        (a: [string, any], b: [string, any]) =>
            (a[1]?.group as string)?.localeCompare(b[1]?.group, 'en') ||
            a[1]?.index - b[1]?.index
    );
    const filteredAttributes = currentAttributes.filter(
        (attribute: [string, any]) =>
            attribute[1] !== null &&
            allowDisplay(attribute[0]) &&
            attribute[1]?.group !== 'Degagements'
    );
    const attributesLeft = filteredAttributes.slice(
        0,
        (filteredAttributes.length + 1) / 2
    );

    const attributesRight = filteredAttributes.slice(
        (filteredAttributes.length + 1) / 2,
        filteredAttributes.length
    );

    const clearanceArray = Object.entries(appliance.attributes)
        .filter((attribute: [string, any]) => {
            return attribute[1] && attribute[1].group === 'Degagements';
        })
        .reverse()
        .sort((a: any, b: any) => {
            if (a[1].index < b[1].index) return -1;
            if (a[1].index > b[1].index) return 1;
            return 0;
        });

    // S'il n'y as pas un nombre pair d'attribut, on créer un attribut vide dans le
    // tableau de droite pour le tout soit bien aligné
    if (attributesLeft.length > attributesRight.length) {
        attributesRight.push([
            'empty',
            {
                group: 'none',
                index: 99999,
                name: { en_CA: '', en_US: '', fr_CA: '' },
                values: [{ en_CA: '', en_US: '', fr_CA: '' }],
            },
        ]);
    }

    return (
        <div className='appliance-specs-container'>
            <div className='appliance-banner'>{t('appliance.specs-title')}</div>
            {!isTablet && isMobile ? (
                <table>
                    <tbody>
                        {filteredAttributes.map((attribute: [string, any]) => (
                            <AttributesElem
                                key={attribute[0]}
                                attribute={attribute[0]}
                            />
                        ))}

                        {ClearanceAttributes(clearanceArray)}
                    </tbody>
                </table>
            ) : (
                <div className='specs-table'>
                    <table>
                        <tbody>
                            {attributesLeft.map((attribute: [string, any]) => (
                                <AttributesElem
                                    key={attribute[0]}
                                    attribute={attribute[0]}
                                />
                            ))}
                        </tbody>
                    </table>
                    <table>
                        <tbody>
                            {attributesRight.map((attribute: [string, any]) => (
                                <AttributesElem
                                    key={attribute[0]}
                                    attribute={attribute[0]}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!isMobile && (
                <table className='clearance-table'>
                    <tbody>{ClearanceAttributes(clearanceArray)}</tbody>
                </table>
            )}

            <span
                className='footnote'
                dangerouslySetInnerHTML={{
                    __html: clearanceFooter?.nodes?.[0]?.attributes?.content
                        ?.valuesMarkdownRemark?.[0][currentLocale],
                }}
            />
            <ul className='spec-footnotes'>{getNoteDesc()}</ul>
        </div>
    );

    function ClearanceAttributes(clearanceArray: any) {
        const displayPipes =
            appliance?.category.includes('Wood Stoves') ||
            appliance?.category.includes('Camp Stoves') ||
            appliance?.category.includes('Cookstoves');

        function stoveTableRow(attribute: any, index: number) {
            return (
                <tr className='clearance-spec-row' key={index}>
                    <th className='spec-title'>
                        {`${t('appliance.clearance')} - ${
                            clearanceArray[index][1].name[currentLocale]
                        }${attribute[0].includes('Des') ? '**' : ''}`}
                    </th>
                    <td className='spec-value'>
                        <span>
                            {
                                clearanceArray[index]?.[1]?.values[0][
                                    currentLocale
                                ]
                            }
                        </span>
                        <span>
                            {
                                clearanceArray[index + 1]?.[1]?.values[0][
                                    currentLocale
                                ]
                            }
                        </span>
                        <span>
                            {
                                clearanceArray[index + 2]?.[1]?.values[0][
                                    currentLocale
                                ]
                            }
                        </span>
                        <span>
                            {
                                clearanceArray[index + 3]?.[1]?.values[0][
                                    currentLocale
                                ]
                            }
                        </span>
                    </td>
                </tr>
            );
        }

        function insertTableRow(attribute: any, index: number) {
            return (
                <tr className='clearance-spec-row' key={index}>
                    <th className='spec-title'>
                        {`${t('appliance.clearance')} - ${
                            clearanceArray[index][1].name[currentLocale]
                        }${attribute[0].includes('Des') ? '**' : ''}`}
                    </th>
                    <td className='spec-value' colSpan={2}>
                        <span>
                            {
                                clearanceArray[index]?.[1]?.values[0][
                                    currentLocale
                                ]
                            }
                        </span>
                        <span>
                            {clearanceArray[index + 1]?.[1]?.values[0].fr_CA}
                        </span>
                    </td>
                </tr>
            );
        }

        const displayCountry =
            appliance?.category.includes('Wood Inserts') ||
            appliance?.category.includes('Wood Furnaces') ||
            appliance?.category.includes('Pellet Stoves') ||
            appliance?.category.includes('Gas Wall Mounted Room Heater');

        return (
            <>
                {(displayPipes || displayCountry) && (
                    <tr className='spec-row countries-row'>
                        <td className='spec-title clearance-title'>
                            <span>{t('appliance.clearance-title')}*</span>
                        </td>
                        <td className='spec-value countries'>
                            <div>
                                <span className='full'>
                                    {t('appliance.canada')}
                                </span>
                                <span className='abr'>
                                    {t('appliance.canada-abr')}
                                </span>
                            </div>
                            <div>
                                <span className='full'>
                                    {t('appliance.usa')}
                                </span>
                                <span className='abr'>
                                    {t('appliance.usa-abr')}
                                </span>
                            </div>
                        </td>
                    </tr>
                )}
                {displayPipes && (
                    <tr className='spec-row'>
                        <td className='spec-title pipes'></td>
                        <td className='spec-value pipes'>
                            <span>{t('appliance.single-pipe')}</span>
                            <span>{t('appliance.double-pipe')}</span>
                            <span>{t('appliance.single-pipe')}</span>
                            <span>{t('appliance.double-pipe')}</span>
                        </td>
                    </tr>
                )}

                {displayPipes
                    ? clearanceArray.map((attribute: any, index: number) => {
                          return (
                              index % 4 === 0 && stoveTableRow(attribute, index)
                          );
                      })
                    : clearanceArray.map((attribute: any, index: number) => {
                          return (
                              index % 2 === 0 &&
                              insertTableRow(attribute, index)
                          );
                      })}
            </>
        );
    }
    function getNoteDesc() {
        return Array.from(textndIds).map((elem, index) => {
            return (
                <li
                    key={index}
                    ref={notesReff}
                >{`${elem[1]?.index}. ${elem[1]?.text[currentLocale]}`}</li>
            );
        });
    }
    function AttributesElem(props: { attribute: string }) {
        if (
            props.attribute === 'empty' ||
            props.attribute === 'vide' ||
            props.attribute === '--'
        ) {
            return (
                <tr className='spec-row'>
                    <td className='spec-title'></td>
                    <td className='spec-value'></td>
                </tr>
            );
        }
        return (
            appliance?.attributes?.[props.attribute] && (
                <tr className='spec-row'>
                    <td className='spec-title'>
                        {
                            appliance?.attributes?.[props.attribute]?.name?.[
                                currentLocale
                            ]
                        }
                        {getApplianceIndex(props.attribute)}
                    </td>
                    <td className='spec-value'>
                        {
                            appliance?.attributes?.[props.attribute]
                                ?.values?.[0]?.[currentLocale]
                        }
                    </td>
                </tr>
            )
        );
    }
    function getApplianceIndex(arg0: string): React.ReactNode {
        const specification = specificationMap.get(arg0);

        if (specification) {
            // eslint-disable-next-line prefer-const
            let linkedIndex: any[] = [];
            const valuesArr = specification?.options?.values || [];
            valuesArr.forEach((value: any) => {
                linkedIndex.push(textndIds.get(value.id)?.index);
            });
            return (
                <span className='note-indicator'>
                    {linkedIndex.map((numb: any) => {
                        return (
                            <span className='notetxt' key={numb}>
                                {' '}
                                (
                                <span onClick={() => scrollToRef(notesReff)}>
                                    {numb}
                                </span>
                                )
                            </span>
                        );
                    })}
                </span>
            );
        }
        return '';
    }
    function isPresentInValues(spec: any) {
        const presentSpecs = Object.entries(appliance.attributes).filter(
            (attribute: any) => {
                return (
                    attribute[1] &&
                    (attribute[1]?.group === 'Appliance performance' ||
                        attribute[1]?.group === 'General features')
                );
            }
        );

        return presentSpecs.find((presentSpec) => presentSpec[0] === spec);
    }
}
